// Copyright (c) StrangeBee 2024

import client from '@Client';
import { findProfileList } from '@Helpers/profile';
import useListQuery from '@Hooks/useListQuery';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import THSelector from '@Molecules/THSelector/THSelector';
import React, { useState, ReactElement, useEffect, useMemo } from 'react';
import { UserOrganisationInput, OrganisationOutput, ProfileOutput } from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';

import './GlobalUserOrgSelector.scss';

interface GlobalUserOrgSelectorProps {
    value?: UserOrganisationInput[];
    onChange?: (value: UserOrganisationInput[]) => void;
    userDefaultOrganisation?: string;
    exceededQuota?: boolean;
}

export const GlobalUserOrgSelector = ({
    value = [],
    onChange,
    userDefaultOrganisation = '',
    exceededQuota = false,
}: GlobalUserOrgSelectorProps): ReactElement => {
    const i = useLocalMessage();

    const { data: everyOrgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listOrganisation' }],
                name: 'get-every-organisations',
            },
        },
        controller: client.OrganisationController,
    });

    let { data: profileList = [] } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listProfile' }],
                name: 'get-all-profiles',
            },
            sort: ['+name'],
        },
        controller: client.ProfileController,
    });

    if (exceededQuota) {
        profileList = profileList.filter((profile) => ['read-only', 'admin'].includes(profile.name));
    }

    const orgList = everyOrgList;

    function orgIsSelected(): OrganisationOutput[] {
        return orgList.filter(({ name }) => value.find(({ organisation }) => name === organisation));
    }
    const [selected, setSelected] = useState<OrganisationOutput[]>(orgIsSelected());

    const [profiles, setProfiles] = useState<{ [key: string]: string }>(
        value.reduce((acc, { organisation, profile }) => ({ ...acc, [organisation]: profile }), {}),
    );

    const [defaultOrganisation, setDefaultOrganisation] = useState<string>(userDefaultOrganisation);

    useEffect(() => {
        setDefaultOrganisation(userDefaultOrganisation);
    }, [userDefaultOrganisation]);

    useEffect(() => {
        //To prevent render with empty new object
        if (everyOrgList.length) {
            setSelected(orgIsSelected());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [everyOrgList]);

    const dataSourceParsed = useMemo(
        () =>
            orgList.filter(({ locked }) => !locked).sort(({ name: orgA }, { name: orgB }) => orgA.localeCompare(orgB)),
        [orgList],
    );

    return (
        <THSelector<OrganisationOutput>
            dataSource={dataSourceParsed}
            refKey="_id"
            value={selected}
            onChange={(newValues) => {
                const newUserOrgInput = newValues.map(({ name }) => ({
                    organisation: name,
                    profile: profiles[name],
                }));

                const newItem = newValues.filter((x) => !selected.find(({ _id }) => _id === x._id)).find(Boolean);

                setSelected(newValues);

                if (newItem) {
                    const newProfiles = {
                        ...profiles,
                        [newItem.name]: findProfileList({ orgName: newItem.name, profileList }).map(
                            ({ name }) => name,
                        )[0],
                    };

                    onChange?.(
                        newValues.map(({ name }) => ({
                            organisation: name,
                            profile: newProfiles[name],
                        })),
                    );
                    setProfiles(newProfiles);

                    return;
                }

                if (!newValues.length) {
                    onChange?.(newUserOrgInput);
                    setProfiles({});
                    return;
                }

                onChange?.(newUserOrgInput);
                setProfiles(
                    newUserOrgInput.reduce(
                        (acc, { organisation, profile }) => ({ ...acc, [organisation]: profile }),
                        {},
                    ),
                );
            }}
            className="global-user-org-selector"
            customSelect={(item, set) => !!set.find(({ _id }) => _id === item._id)}
        >
            {(item, isSelected) => (
                <div className="global-user-org-selector__item">
                    <div className="global-user-org-selector__name">
                        {isSelected ? <ICONS.CheckCircleTwoTone /> : <ICONS.MinusCircleOutlined />}
                        <span className={classList('global-user-org-selector__name__default')}>{item.name}</span>
                    </div>

                    {defaultOrganisation !== item.name && (
                        <THButton
                            type="link"
                            className="global-user-org-selector__button__set"
                            onClick={(event) => {
                                if (isSelected) {
                                    event.stopPropagation();
                                }

                                onChange?.(
                                    value.map((organisation) => ({
                                        ...organisation,
                                        default: item.name === organisation.organisation,
                                    })),
                                );
                                setDefaultOrganisation(item.name);
                            }}
                        >
                            {i('user.create.form.organisations.set-as-default')}
                        </THButton>
                    )}

                    {defaultOrganisation === item.name && (
                        <THButton className="global-user-org-selector__button__default" type="link" disabled>
                            {i('user.create.form.organisations.default')}
                        </THButton>
                    )}

                    <div className="global-user-org-selector__profile">
                        <THSelect
                            errorless
                            value={profiles[item.name]}
                            onChange={(newProfile) => {
                                setProfiles((prev) => {
                                    const newProfiles = { ...prev, [item.name]: newProfile };

                                    if (!isSelected) {
                                        const newValues = [...selected, item];
                                        onChange?.(
                                            newValues.map(({ name }) => ({
                                                organisation: name,
                                                profile: newProfiles[name],
                                            })),
                                        );
                                        setSelected(newValues);
                                    } else {
                                        onChange?.(
                                            value.map(({ organisation }) => ({
                                                organisation,
                                                profile: newProfiles[organisation],
                                            })),
                                        );
                                    }

                                    return newProfiles;
                                });
                            }}
                            onClick={(event) => event.stopPropagation()}
                            options={findProfileList({ orgName: item.name, profileList }).map(({ name }) => ({
                                label: name,
                                value: name,
                            }))}
                        />
                    </div>
                </div>
            )}
        </THSelector>
    );
};
